<template>
    <div>
        <v-container>
            <div class="text-h5 text-sm-h5 text-md-h4 text-lg-h4 ma-3 text-xl-h4 text-center">
                Pedidos Material Auxiliar
            </div>

            <v-form :disabled="loading">
                <v-row align="center">

                    <!-- CIUDAD -->
                    <v-col class="mb-n7" cols="12" sm="6" md="3" lg="2" xl="2">
                        <v-select
                            v-model="filtros.CiudadId"
                            :items="ciudades"
                            item-text="Nombre"
                            item-value="CiudadId"
                            label="Ciudad"
                            outlined
                            dense
                            clearable
                            hide-details
                            color="primary"
                            class="font-weight-bold"
                            :loading="loadingCiudades"
                        ></v-select>
                    </v-col>

                    <!-- RUTA — siempre visible, solo activo en ciudades con ruta (Zaragoza) -->
                    <v-col class="mb-n7" cols="12" sm="6" md="3" lg="2" xl="2">
                        <v-select
                            v-model="filtros.Ruta"
                            :items="rutasItems"
                            item-text="text"
                            item-value="value"
                            label="Ruta"
                            outlined
                            dense
                            clearable
                            hide-details
                            color="primary"
                            :disabled="!ciudadTieneRuta || loadingRutas"
                            class="font-weight-bold"
                            :loading="loadingRutas"
                        ></v-select>
                    </v-col>

                    <!-- DESDE FECHA -->
                    <v-col class="mb-n7" cols="6" sm="4" md="3" lg="2" xl="2">
                        <DateFind label="Desde" v-model="filtros.FechaDesde" color="primary" class="font-weight-bold"></DateFind>
                    </v-col>

                    <!-- DÍA ANTERIOR / SIGUIENTE -->
                    <v-col cols="auto" class="pb-0 pt-5">
                        <v-btn icon color="primary" :disabled="loading || !filtros.FechaDesde" @click="diaAnterior">
                            <v-icon>mdi-chevron-left</v-icon>
                        </v-btn>
                        <v-btn icon color="primary" :disabled="loading || !filtros.FechaDesde" @click="diaSiguiente">
                            <v-icon>mdi-chevron-right</v-icon>
                        </v-btn>
                    </v-col>

                    <!-- SOLO SIN CERRAR -->
                    <v-col cols="auto" class="pb-0 pt-5">
                        <v-switch
                            v-model="filtros.SoloSinCerrar"
                        
                            class="mt-0 pt-0"
                            hide-details
                            color="primary"
                        >
                            <template v-slot:label>
                                <span class="font-weight-bold">Solo sin cerrar</span>
                            </template>                         
                        </v-switch>
                    </v-col>

                    <!-- BOTÓN IMPRIMIR RESUMEN PLANTILLAS -->
                    <v-col cols="auto" class="pb-0 pt-4">
                        <v-btn
                            :disabled="!filtros.CiudadId || loading"
                            :loading="loadingImprimir"
                            color="primary"
                            @click="imprimirResumen"
                            class="font-weight-bold"
                        >
                            <v-icon left>mdi-printer</v-icon>
                            Resumen plantillas
                        </v-btn>
                    </v-col>

                </v-row>
            </v-form>

            <v-alert v-if="!filtros.CiudadId" type="info" outlined class="mt-4">
                Selecciona una ciudad para cargar los pedidos.
            </v-alert>

            <v-data-table 
                v-if="filtros.CiudadId"
                :headers="headers"
                :items="dataRows"
                :options.sync="options"
                :footer-props="{ itemsPerPageOptions: [15, 30, 50, 100] }"
                :server-items-length="totalDataRows"
                :loading="loading"
                @click:row="onClickRowEditar"
                style="overflow: scroll; margin-top: 20px"
                mobile-breakpoint="0"
                class="tabla-mataux"
                dense
            >
                <!-- eslint-disable-next-line vue/valid-v-slot -->
                <template v-slot:item.FechaPedido="{ item }">
                    {{ formatDate(item.FechaPedido) }}
                </template>

                <!-- eslint-disable-next-line vue/valid-v-slot -->
                <template v-slot:item.Cerrado="{ item }">
                    <v-icon v-if="item.Cerrado" color="success">mdi-check-circle</v-icon>
                    <v-icon v-else color="grey lighten-1">mdi-circle-outline</v-icon>
                </template>

                <!-- eslint-disable-next-line vue/valid-v-slot -->
                <template v-slot:item.Pendiente="{ item }">
                    <v-chip v-if="item.Pendiente" color="warning" small label>Pendiente</v-chip>
                </template>

            </v-data-table>

        </v-container>
    </div>
</template>

<script>
import axios from "axios";
import DateFind from "../components/DateFind.vue";
import { mapState } from "vuex";

const STORAGE_KEY = 'mataux_filtros';

export default ({
    components: { DateFind },
    computed: {
        ...mapState(['empId', 'dirId', 'perId', 'urlRaiz']),

        // Ciudad seleccionada (objeto completo del array ciudades)
        ciudadSeleccionada () {
            return this.ciudades.find(c => c.CiudadId === this.filtros.CiudadId) || null;
        },

        // True si la ciudad actual tiene filtro de ruta (Zaragoza).
        // Se comprueba tanto tieneRuta (camelCase) como TieneRuta (PascalCase)
        // porque el serializador del proyecto puede devolver cualquiera de los dos.
        ciudadTieneRuta () {
            if (!this.ciudadSeleccionada) return false;
            return this.ciudadSeleccionada.tieneRuta === true
                || this.ciudadSeleccionada.TieneRuta === true;
        },

        // Items del combo de ruta
        rutasItems () {
            return this.rutas.map(r => ({ text: r || '(Sin ruta)', value: r }));
        }
    },
    data () {
        return {
            loading:         false,
            loadingCiudades: false,
            loadingRutas:    false,
            loadingImprimir: false,
            totalDataRows:   0,
            dataRows:        [],
            options:         { itemsPerPage: 15 },
            headers: [
                { text: 'Tienda',        width: 250, sortable: false, value: 'NombreRemoto'  },
                { text: 'Fecha',         width: 110, sortable: false, value: 'FechaPedido'   },
                { text: 'Cerrado',       width: 90,  sortable: false, value: 'Cerrado',  align: 'center' },
                { text: 'Pendiente',     width: 110, sortable: false, value: 'Pendiente'     },
                { text: 'Observaciones',             sortable: false, value: 'Observaciones' }
            ],
            dtOptionsLoaded: false,
            ciudades:        [],
            rutas:           [],
            filtros: {
                CiudadId:      null,
                FechaDesde:    null,
                SoloSinCerrar: false,
                Ruta:          null
            }
        }
    },
    watch: {
        options: {
            handler () {
                if (!this.dtOptionsLoaded) { this.dtOptionsLoaded = true; return; }
                if (this.filtros.CiudadId) this.loadData();
            },
            deep: true
        },
        'filtros.CiudadId' (newVal, oldVal) {
            // Al cambiar ciudad: limpiar ruta y recargar rutas si la nueva ciudad las tiene
            if (newVal !== oldVal) {
                this.filtros.Ruta = null;
                this.rutas        = [];
                if (this.ciudadTieneRuta) this.loadRutas();
            }
            this.resetAndLoad();
        },
        'filtros.FechaDesde'    () { this.resetAndLoad(); },
        'filtros.SoloSinCerrar' () { this.resetAndLoad(); },
        'filtros.Ruta'          () { this.resetAndLoad(); }
    },
    mounted () {
        // Restaurar filtros guardados al volver de la edición de un pedido
        const saved = sessionStorage.getItem(STORAGE_KEY);
        if (saved) {
            try { this.filtros = JSON.parse(saved); } catch (e) { this.setFechaDefecto(); }
            sessionStorage.removeItem(STORAGE_KEY);
        } else {
            this.setFechaDefecto();
        }
        this.loadCiudades();
    },
    methods: {

        // ── Fecha por defecto: lunes de la semana actual ─────────────────────────
        setFechaDefecto () {
            const hoy  = new Date();
            const diff = hoy.getDay() === 0 ? -6 : 1 - hoy.getDay();
            const lunes = new Date(hoy);
            lunes.setDate(hoy.getDate() + diff);
            this.filtros.FechaDesde = this.dateToStr(lunes);
        },

        dateToStr (date) {
            return date.getFullYear() + '-'
                + String(date.getMonth() + 1).padStart(2, '0') + '-'
                + String(date.getDate()).padStart(2, '0');
        },

        diaAnterior () {
            const d = new Date(this.filtros.FechaDesde + 'T00:00:00');
            d.setDate(d.getDate() - 1);
            this.filtros.FechaDesde = this.dateToStr(d);
        },

        diaSiguiente () {
            const d = new Date(this.filtros.FechaDesde + 'T00:00:00');
            d.setDate(d.getDate() + 1);
            this.filtros.FechaDesde = this.dateToStr(d);
        },

        resetAndLoad () {
            if (!this.filtros.CiudadId) return;
            this.options.page = 1;
            this.loadData();
        },

        // ── Ciudades ─────────────────────────────────────────────────────────────
        loadCiudades () {
            this.loadingCiudades = true;
            const AuthToken = localStorage.getItem('token');
            axios({
                method: "POST",
                url: this.urlRaiz + "/api/mataux/ciudades",
                headers: { "content-type": "application/json", "Authorization": AuthToken }
            })
            .then(result => {
                // Normalizamos los campos para evitar problemas camelCase/PascalCase
                // El serializador del proyecto puede devolver TieneRuta o tieneRuta
                this.ciudades = (result.data.Ciudades || []).map(c => ({
                    CiudadId:  c.CiudadId  ?? c.ciudadId,
                    Nombre:    c.Nombre    ?? c.nombre,
                    // Aceptamos tanto camelCase como PascalCase y normalizamos a camelCase
                    tieneRuta: c.tieneRuta ?? c.TieneRuta ?? false
                }));

                // Si al restaurar filtros la ciudad seleccionada tiene rutas, cargarlas ahora
                if (this.ciudadTieneRuta && !this.rutas.length) {
                    this.loadRutas();
                }
            })
            .catch(() => { alert('Error al cargar las ciudades.'); })
            .finally(() => { this.loadingCiudades = false; });
        },

        // ── Rutas (solo para ciudades con TieneRuta = true, actualmente Zaragoza) ─
        loadRutas () {
            this.loadingRutas = true;
            const AuthToken = localStorage.getItem('token');
            axios({
                method: "POST",
                url: this.urlRaiz + "/api/mataux/rutas",
                data: JSON.stringify({ CiudadId: this.filtros.CiudadId }),
                headers: { "content-type": "application/json", "Authorization": AuthToken }
            })
            .then(result => { this.rutas = result.data.Rutas || []; })
            .catch(() => { alert('Error al cargar las rutas.'); })
            .finally(() => { this.loadingRutas = false; });
        },

        // ── Pedidos ──────────────────────────────────────────────────────────────
        getDataFromApi () {
            this.loading = true;
            return new Promise((resolve) => {
                const params = {
                    NumRegsPag:    this.options.itemsPerPage,
                    NumPag:        this.options.page,
                    CiudadId:      this.filtros.CiudadId,
                    FechaDesde:    this.filtros.FechaDesde,
                    SoloSinCerrar: this.filtros.SoloSinCerrar,
                    // Solo enviamos ruta si la ciudad la soporta
                    Ruta:          this.ciudadTieneRuta ? (this.filtros.Ruta || null) : null
                };
                const AuthToken = localStorage.getItem('token');
                axios({
                    method: "POST",
                    url: this.urlRaiz + "/api/mataux/list",
                    data: JSON.stringify(params),
                    headers: { "content-type": "application/json", "Authorization": AuthToken }
                })
                .then(result => {
                    setTimeout(() => {
                        this.loading = false;
                        resolve({ items: result.data.EntsList, total: result.data.EntsTotCount });
                    }, 300);
                })
                .catch((err) => {
                    this.loading = false;
                    alert(JSON.stringify(err.response?.data ?? err.message));
                });
            });
        },

        loadData () {
            this.getDataFromApi().then(data => {
                this.dataRows      = data.items;
                this.totalDataRows = data.total;
            });
        },

        // ── Navegación al detalle: guardamos filtros antes de salir ──────────────
        onClickRowEditar (item) {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.filtros));
            this.$router.push({
                path: '/mataux-editar',
                query: {
                    idPedido:   item.idPedido   ?? item.IdPedido,
                    idRemoto:   item.idRemoto   ?? item.IdRemoto,
                    codEmpresa: item.codEmpresa ?? item.CodEmpresa,
                    ciudadId:   this.filtros.CiudadId
                }
            });
        },

        
        // ── Imprimir resumen de plantillas ────────────────────────────────────────
        // 1. Abre diálogo de impresión/guardar PDF (se cierra solo al terminar)
        // 2. Envía el PDF por email
        imprimirResumen () {
            this.loadingImprimir = true;
            const AuthToken = localStorage.getItem('token');
            const payload = {
                CiudadId:      this.filtros.CiudadId,
                FechaDesde:    this.filtros.FechaDesde,
                SoloSinCerrar: this.filtros.SoloSinCerrar,
                Ruta:          this.ciudadTieneRuta ? (this.filtros.Ruta || null) : null
            };
            axios({
                method: "POST",
                url: this.urlRaiz + "/api/mataux/resumen-plantillas",
                data: JSON.stringify(payload),
                headers: { "content-type": "application/json", "Authorization": AuthToken }
            })
            .then(result => {
                const lineas = result.data.Lineas || [];
                if (!lineas.length) {
                    alert('No hay datos para imprimir.');
                    return;
                }
                // Enviar email con el PDF generado desde el RDLC
                this.enviarEmailResumen(payload);
                // Abrir diálogo guardar PDF (ventana se cierra sola al terminar)
                this.abrirVentanaImpresion(lineas);
                
            })
            .catch(() => { alert('Error al obtener el resumen de plantillas.'); })
            .finally(() => { this.loadingImprimir = false; });
        },

        // Abre ventana de impresión/guardar PDF y la cierra automáticamente al terminar
        abrirVentanaImpresion (lineas) {
            const fecha     = this.formatDate(new Date().toISOString());
            const ciudadNom = this.ciudadSeleccionada?.Nombre || '';
            const rutaNom   = this.filtros.Ruta ? `Ruta: ${this.filtros.Ruta}` : '';

            const tiendas   = [];
            const tiendaMap = {};
            lineas.forEach(l => {
                const key = l.CodigoRemoto || l.codigoRemoto || '';
                if (!tiendaMap[key]) {
                    const t = {
                        nombreRemoto: l.NombreRemoto || l.nombreRemoto || '',
                        lineas:       []
                    };
                    tiendaMap[key] = t;
                    tiendas.push(t);
                }
                tiendaMap[key].lineas.push({
                    articulo: l.Articulo || l.articulo || '',
                    cantidad: parseFloat(l.Cantidad || l.cantidad || 0).toFixed(2)
                });
            });

            let html = `<!DOCTYPE html>
<html lang="es"><head><meta charset="UTF-8"><title>Resumen Plantillas</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;font-size:11px;color:#000;padding:15mm}
  .rh{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:6mm}
  .rt{font-size:14px;font-weight:bold;text-align:center;flex:1}
  .rm{font-size:10px;white-space:nowrap}
  .tb{margin-bottom:6mm;page-break-inside:avoid}
  .th{font-weight:bold;font-size:11px;border-bottom:1px solid #000;padding-bottom:1mm;margin-bottom:2mm}
  .ch{display:flex;justify-content:space-between;padding:1px 2mm;font-weight:bold;background:#555;color:#fff;font-size:10px}
  .lr{display:flex;justify-content:space-between;padding:1px 2mm}
  .lr:nth-child(even){background:#f0f0f0}
  .ar{flex:1;padding-right:4mm}.cr{min-width:50px;text-align:right}
  @media print{.lr:nth-child(even){background:#ebebeb!important;-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body>
  <div class="rh">
    <div class="rm">${ciudadNom}${rutaNom ? '<br>' + rutaNom : ''}</div>
    <div class="rt">RESUMEN PLANTILLAS PARA TIENDAS</div>
    <div class="rm"><strong>${fecha}</strong></div>
  </div>`;

            tiendas.forEach(t => {
                html += `\n  <div class="tb"><div class="th">TIENDA: ${t.nombreRemoto}</div>
  <div class="ch"><span class="ar">Artículo</span><span class="cr">Cantidad</span></div>`;
                t.lineas.forEach(l => {
                    html += `\n  <div class="lr"><span class="ar">${l.articulo}</span><span class="cr">${l.cantidad}</span></div>`;
                });
                html += `\n  </div>`;
            });
            html += `\n</body></html>`;

            const w = window.open('', '_blank', 'width=900,height=700');
            w.document.write(html);
            w.document.close();
            w.focus();
            // Cierra la ventana automáticamente al terminar de imprimir/guardar
            w.onafterprint = () => w.close();
            setTimeout(() => { w.print(); }, 500);
        },

        // Llama al back para generar el PDF desde el RDLC y enviarlo por email
        enviarEmailResumen (payload) {
            const AuthToken = localStorage.getItem('token');
            axios({
                method: "POST",
                url: this.urlRaiz + "/api/mataux/resumen-plantillas-email",
                data: JSON.stringify(payload),
                headers: { "content-type": "application/json", "Authorization": AuthToken }
            })
            .then(result => {
                if (!result.data || !result.data.Success)
                    console.warn('Error al enviar email del resumen:', result.data?.Error);
            })
            .catch(err => { console.warn('Error al enviar email del resumen:', err); });
        },

        formatDate (value) {
            if (!value) return '';
            const d = new Date(value);
            return String(d.getDate()).padStart(2, '0') + '/'
                + String(d.getMonth() + 1).padStart(2, '0') + '/'
                + d.getFullYear();
        }
    }
})
</script>
<style scoped>
.tabla-mataux >>> .v-data-table__wrapper > table > thead > tr > th {
    font-weight: bold !important;
}
.tabla-mataux >>> .v-data-table__wrapper > table > tbody > tr > td {
    font-weight: bold !important;
}
</style>